import { TableRow } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

// COMPONENTS
import BoxGrid from "@/main/components/shared/boxGrid/boxGrid";
import KOGCollapse from "@/main/components/shared/kogCollapse/kogCollapse";
import { RowHeaderTextCenter, SkeletonLoading, SpinnerLoading } from "@/main/components/shared/styles";

// SERVICES / REDUX
import { QuoteHeaderAnswerInformationResponse, QuoteSupplierItemPartResponse } from "@/main/models/response";
import { StatusQuoteSupplierItem } from "@/main/services/enum/StatusQuoteSupplierItem";
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { useWarehouseReducer } from "@/main/store/warehouse";

import KogCheckbox from "@/main/components/shared/checkbox/kogCheckbox";
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";
import { ISelectMultipleOption } from "@/main/components/shared/selectMultipleOutlined/selectMultipleOutlined";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { ICreateAnswerForm } from "../interfaceCreateAnswer";
import LockedNotQuoteCheckbox from "../components/lockedNotQuoteCheckbox/lockedNotQuoteCheckbox";
import TableRowAnswerGrouped from "./TableRowAnswer/TableRowAnswerGrouped";
import { buildSizeColumns } from "./columnConfig";
import { getDisplayedGroupRows, groupPendingItems } from "./groupPendingItems";
import * as S from "./styles";

interface ICardGridAnswerProps {
	dataGrid: QuoteSupplierItemPartResponse[];
	dataHeader?: QuoteHeaderAnswerInformationResponse;
	loading?: boolean;
	hasReadOnly?: boolean;
	idStatus: number;
	openModalOnce: boolean;
	isDeliveryBySupplier: boolean;
	isFinishedTime?: boolean;
	setOpenModalOnce: React.Dispatch<React.SetStateAction<boolean>>;
	handleOpenModalDetails: (guidPart: string) => void;
	updateDataGrid: (data: QuoteSupplierItemPartResponse[]) => void;
	isRequoteNational?: boolean;
	filterByFavorite?: boolean;
	cardTitle?: string;
	isOpenInitial?: boolean;
	isShowModalReasonAll: boolean;
	setIsShowModalReasonAll: React.Dispatch<React.SetStateAction<boolean>>;
	handleOpenModalImages: (item: QuoteSupplierItemPartResponse, allowEdit: boolean) => void;
}
const TitleHeader = ({
	title,
	notRequired = false,
	isTextCenter = false,
	subtitle = null,
}: {
	title: string | React.ReactNode;
	notRequired?: boolean;
	isTextCenter?: boolean;
	subtitle?: string | null;
}) => (
	<S.RowTitleHeader>
		{isTextCenter ? (
			<RowHeaderTextCenter>
				{title}
				{!notRequired && <S.TextAsterisk> *</S.TextAsterisk>}
				<p>{subtitle}</p>
			</RowHeaderTextCenter>
		) : (
			<S.ContainerTitle>
				<p>
					{title} {!notRequired && <S.TextAsterisk> *</S.TextAsterisk>}
				</p>
				<p>{subtitle}</p>
			</S.ContainerTitle>
		)}
	</S.RowTitleHeader>
);
function GridAnswer({
	dataGrid,
	dataHeader,
	loading = false,
	hasReadOnly = false,
	openModalOnce,
	isFinishedTime,
	setOpenModalOnce,
	isShowModalReasonAll,
	setIsShowModalReasonAll,
	handleOpenModalDetails,
	isDeliveryBySupplier = true,
	filterByFavorite,
	handleOpenModalImages,
}: ICardGridAnswerProps) {
	const { control, setValue, getValues } = useFormContext<ICreateAnswerForm>();
	const { fields } = useFieldArray({ control, name: "pendingItems" });

	const [allSelected, setAllSelected] = useState<boolean>(false);
	const isSelfSupplied = Boolean(dataHeader?.selfSupplied);
	const [selectedPartTypesByGroup, setSelectedPartTypesByGroup] = useState<Record<string, ISelectMultipleOption[]>>({});

	const { warehouse: warehouseReducer } = useWarehouseReducer();

	const haveOnlyOneWarehouse = warehouseReducer?.data?.length === 1;

	const watchedPendingItems = useWatch({ control, name: "pendingItems" });
	const hasNotQuoteSelected = useMemo(
		() => Array.isArray(watchedPendingItems) && watchedPendingItems.some((item) => item?.notQuote),
		[watchedPendingItems],
	);

	const allGroups = useMemo(() => groupPendingItems(dataGrid), [dataGrid]);

	const groups = useMemo(() => {
		if (filterByFavorite === undefined) return allGroups;
		return allGroups.filter((group) => {
			const isFavorite = group.rows[0]?.item.isFavorite ?? false;
			return filterByFavorite ? isFavorite : !isFavorite;
		});
	}, [allGroups, filterByFavorite]);

	const displayedItems = useMemo(
		() =>
			groups.flatMap((group) =>
				getDisplayedGroupRows(group, selectedPartTypesByGroup[group.groupKey] ?? []).map(({ item }) => item),
			),
		[groups, selectedPartTypesByGroup],
	);

	const hasInventoryDetails = useMemo(
		() => displayedItems.some((item) => !isNullUndefined(item.guidSupplierInventory)),
		[displayedItems],
	);

	const hasNoPriceWarning = useMemo(
		() => displayedItems.some((item) => Boolean(item.showNoPriceWarning)),
		[displayedItems],
	);

	const hasActionColumn = hasInventoryDetails || hasNoPriceWarning;

	const sizeColumns = useMemo(
		() =>
			buildSizeColumns({
				haveOnlyOneWarehouse,
				isDeliveryBySupplier,
				havePartInventory: hasInventoryDetails,
				hasNotQuoteSelected,
				hasCollumnAction: hasNoPriceWarning,
			}),
		[haveOnlyOneWarehouse, isDeliveryBySupplier, hasInventoryDetails, hasNotQuoteSelected, hasNoPriceWarning],
	);

	function handleSelectedPartTypesChange(groupKey: string, selected: ISelectMultipleOption[]) {
		setSelectedPartTypesByGroup((previous) => ({ ...previous, [groupKey]: selected }));
	}

	function handleToggleSelectAll() {
		const pendingItems = getValues("pendingItems");

		if (allSelected) {
			pendingItems.forEach((item, index) => {
				if (item.notQuote) {
					setValue(`pendingItems.${index}.notQuote`, false);
					setValue(`pendingItems.${index}.reason`, null);
					setValue(`pendingItems.${index}.observation`, "");
				}
			});
			setAllSelected(false);
			return;
		}
		pendingItems.forEach((item, index) => {
			if (!item.notQuote && item.isPendingAnswer && !item.isNagsLoading) {
				setValue(`pendingItems.${index}.notQuote`, true);
				setValue(`pendingItems.${index}.backOrder`, false);
			}
		});
		setAllSelected(true);
	}

	function getMaximumLengthHeader(string: string, maxchars: number): string {
		return string.length > maxchars + 1 ? `${string.substring(0, maxchars)}.` : string;
	}

	const rowsHeader = [
		<TitleHeader key="requestedPart" title={getTranslateByKey("information.requestedPart")} />,
		<TitleHeader key="partNumber" title={getTranslateByKey("information.partNumber")} notRequired />,
		<TitleHeader key="partTypes" title={getTranslateByKey("information.partTypes")} />,
		!haveOnlyOneWarehouse && <TitleHeader key="warehouse" title={getTranslateByKey("information.warehouse")} />,
		<TitleHeader key="sku" title={getTranslateByKey("information.sku")} notRequired />,
		<TitleHeader key="amount" title={getTranslateByKey("information.amount")} />,
		<TitleHeader key="price" title={getTranslateByKey("information.price")} />,
		<TitleHeader key="pickupDaysDeadline" title={getTranslateByKey("information.pickupDaysDeadline")} />,
		!isDeliveryBySupplier && (
			<TitleHeader
				key="dimensions"
				title={`${getTranslateByKey("partDetail.dimensions")}`}
				subtitle={`${getMaximumLengthHeader(getTranslateByKey("information.height"), 4)} x ${getMaximumLengthHeader(
					getTranslateByKey("information.width"),
					4,
				)} x ${getMaximumLengthHeader(getTranslateByKey("information.depth"), 4)}`}
			/>
		),
		!isDeliveryBySupplier && (
			<TitleHeader key="weight" title={getTranslateByKey("information.weight")} subtitle="(kg)" />
		),
		<S.TitleHeaderCheckbox key="backOrder">{getTranslateByKey("information.backOrder")}</S.TitleHeaderCheckbox>,
		<S.TitleHeaderCheckbox key="notQuote">
			{getTranslateByKey("information.notQuote")}
			{!hasReadOnly && isSelfSupplied && (
				<LockedNotQuoteCheckbox
					id="id-checkbox-selectAll"
					checked={false}
					tooltipMessage={getTranslateByKey("information.doNotQuoteBlockedTooltip")}
				/>
			)}
			{!hasReadOnly && !isSelfSupplied && (
				<KogCheckbox
					disabled={hasReadOnly}
					key="checkbox"
					checked={allSelected}
					value={allSelected}
					onChange={handleToggleSelectAll}
					id="id-checkbox-selectAll"
				/>
			)}
		</S.TitleHeaderCheckbox>,
		hasActionColumn ? <TitleHeader title="" notRequired /> : false,
	].filter(Boolean);

	function checkAllNotQuoteTrue(data: QuoteSupplierItemPartResponse[]) {
		return data.every((item) => item.notQuote);
	}

	useEffect(() => {
		if (dataGrid && dataGrid.length != 0 && checkAllNotQuoteTrue(dataGrid)) {
			setAllSelected(true);
		}
	}, [dataGrid]);

	if (!loading && groups.length === 0) {
		return <ResultNotFoundComponent />;
	}

	return (
		<S.ContainerGrid>
			<BoxGrid rowHeaders={rowsHeader} typeGrid="backgroundBody" sizeColumns={sizeColumns}>
				{loading
					? Array.from(new Array(4)).map((_, index) => (
							<TableRow key={index}>
								{rowsHeader.map((_, index) => (
									<S.TableCellGrid key={index} $showMobile>
										<SkeletonLoading />
									</S.TableCellGrid>
								))}
							</TableRow>
						))
					: groups.map((group) => (
							<TableRowAnswerGrouped
								key={group.groupKey}
								group={group}
								fieldIds={group.rows.map(({ originalIndex }) => fields[originalIndex]?.id)}
								hasReadOnly={hasReadOnly}
								dataHeader={dataHeader}
								isFinishedTime={isFinishedTime}
								openModalOnce={openModalOnce}
								setOpenModalOnce={setOpenModalOnce}
								isShowModalReasonAll={isShowModalReasonAll}
								setIsShowModalReasonAll={setIsShowModalReasonAll}
								handleOpenModalDetails={handleOpenModalDetails}
								setAllSelected={setAllSelected}
								allSelected={allSelected}
								handleOpenModalImages={handleOpenModalImages}
								hasActionColumn={hasActionColumn}
								selectedOptions={selectedPartTypesByGroup[group.groupKey] ?? []}
								onSelectedOptionsChange={(selected) => handleSelectedPartTypesChange(group.groupKey, selected)}
							/>
						))}
			</BoxGrid>
		</S.ContainerGrid>
	);
}
function CardGridAnswerPending(props: ICardGridAnswerProps) {
	const title = props.cardTitle
		? props.cardTitle
		: props.idStatus === StatusQuoteSupplierItem.Answered
			? getTranslateByKey("information.partsAnswered")
			: getTranslateByKey("information.partsPending");

	return (
		<S.Container>
			<KOGCollapse
				isOpenCollapseinitial={props.isOpenInitial ?? true}
				showBorder
				keepMounted
				header={
					<S.AreaTitleCard>
						<S.TitleCard>{title}</S.TitleCard>
						{props.loading && <SpinnerLoading size="xsmall" noMargin />}
					</S.AreaTitleCard>
				}
				content={<GridAnswer {...props} />}
			/>
		</S.Container>
	);
}

export default CardGridAnswerPending;
